import React, { useMemo, useState } from "react";
import styles from "./TestRuleModal.module.scss";
import { IModalInstance } from "@whatslly/ui-components/modals/ModalProvider";
import { BaseModal, ModalHeader, modalService } from "@whatslly/ui-components/modals";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { Button } from "@whatslly/ui-components/v2/Button";
import { TextareaAutosize } from "@mui/material";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { MdCancel, MdCheckCircle } from "react-icons/md";
import { IoMdAdd, IoMdClose } from "react-icons/io";
import classNames from "classnames";
import type { DLPRuleExpressionType, DLPRuleMetaDataType, DLPRulePIIType } from "@whatslly/types/dlp/DLPRule";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { TestRuleResponse } from "@whatslly/types/integrations/dlp";
import { DlpExtractMetadataResponse } from "@whatslly/types/ocr/DlpTextExtractionTypes";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { postJSON } from "@whatslly/commons/apiUtils";
import { parseOrReturnError } from "../../../services/utils";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { getPIICategories } from "../DLPPIICategories";
import { UploadFilesV2 } from "@whatslly/ui-components/v2/UploadFiles/UploadFilesV2";

type ConditionType = DLPRuleExpressionType | DLPRulePIIType | DLPRuleMetaDataType;

interface TestRuleModalProps extends IModalInstance<{ condition: ConditionType }> {
  condition: ConditionType;
}

interface TestRow {
  id: number;
  text: string;
  result?: boolean;
}

const TestResultStatus: React.FC<{ succeed?: boolean }> = ({ succeed }) => {
  if (succeed === undefined) return null;
  return (
    <div className={styles.testResultStatus} data-testid={`test-rule-result-${succeed ? "succeed" : "failed"}`}>
      {succeed ? <MdCheckCircle /> : <MdCancel />}
    </div>
  );
};

export const TestRuleModal: React.FC<TestRuleModalProps> = observer(({ closeModal, condition }) => {
  const { t } = useTranslation();
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const logger = useDependencyInjector<FrontLogger>(FrontLogger);
  const PIICategories = useMemo(() => getPIICategories(t), [t]);

  const CASE_SENSITIVE_SELECT_OPTIONS = useMemo(
    () => [
      { label: t("dlp_page.conditions.expression_casesensitive"), value: true },
      { label: t("test_regex_modal.not_case_sensitive"), value: false },
    ],
    [t]
  );

  const [conditionState, setConditionState] = useState<ConditionType>(condition);
  const [rows, setRows] = useState<TestRow[]>([{ id: 1, text: "", result: undefined }]);
  const [loading, setLoading] = useState(false);
  const [nextId, setNextId] = useState(2);
  const [metadataFile, setMetadataFile] = useState<File | null>(null);
  const [metadataTestResult, setMetadataTestResult] = useState<boolean | undefined>(undefined);
  const [extractedMetadata, setExtractedMetadata] = useState<Record<string, string> | null>(null);

  const isExpression = conditionState.type === "expression";
  const isPII = conditionState.type === "PII";
  const isMetadata = conditionState.type === "metadata";

  const categoryLabel = useMemo(() => {
    if (!isPII) return "";
    const piiCondition = conditionState as DLPRulePIIType;
    return PIICategories.find((cat) => cat.value === piiCondition.category)?.label ?? piiCondition.category;
  }, [PIICategories, conditionState, isPII]);

  const modalTitle = isExpression
    ? t("test_regex_modal.test_regex_modal_title")
    : isMetadata
      ? t("test_rule_modal.test_metadata_title")
      : t("test_pii_modal.test_pii_modal_title");

  const MAX_TEST_ROWS = 4;

  const addRow = () => {
    if (rows.length >= MAX_TEST_ROWS) return;
    setRows([...rows, { id: nextId, text: "", result: undefined }]);
    setNextId(nextId + 1);
  };

  const removeRow = (id: number) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((row) => row.id !== id));
  };

  const updateRowText = (id: number, text: string) => {
    setRows(rows.map((row) => (row.id === id ? { ...row, text, result: undefined } : row)));
  };

  const loadMetadata = async () => {
    if (!metadataFile) return;
    try {
      setLoading(true);
      setMetadataTestResult(undefined);
      const form = new FormData();
      form.append("file", metadataFile, metadataFile.name);
      const res = await postJSON({
        urlSuffix: "/integrations/dlp/extract-metadata",
        etoken: userStore.etoken ?? "",
        body: form,
      }).then((r) => parseOrReturnError<DlpExtractMetadataResponse>(r, logger));
      setLoading(false);
      if (!res.success || !("metadata" in res)) {
        modalService.openModal(Toast, { text: t("test_rule_modal.failed_to_test") });
        return;
      }
      setExtractedMetadata(res.metadata);
    } catch {
      setLoading(false);
      modalService.openModal(Toast, { text: t("test_rule_modal.failed_to_test") });
    }
  };

  const runTest = async () => {
    if (isMetadata) {
      const meta = conditionState as DLPRuleMetaDataType;
      const value = extractedMetadata?.[meta.key];
      setMetadataTestResult(String(value) === String(meta.value));
      return;
    }

    const textsToTest = rows.filter((row) => row.text.trim()).map((row) => row.text);
    if (textsToTest.length === 0) return;

    try {
      setLoading(true);
      const res = await postJSON({
        urlSuffix: "/integrations/dlp/rule/test",
        etoken: userStore.etoken ?? "",
        body: JSON.stringify({ condition: conditionState, texts: textsToTest }),
      }).then((r) => parseOrReturnError<TestRuleResponse>(r, logger));
      setLoading(false);
      if (!res.success) {
        modalService.openModal(Toast, { text: t("test_rule_modal.failed_to_test") });
        return;
      }
      // Update results for each row
      setRows(
        rows.map((row) => {
          if (!row.text.trim()) return row;
          const resultItem = res.results.find((r) => r.text === row.text);
          return { ...row, result: resultItem?.matches };
        })
      );
    } catch {
      setLoading(false);
      modalService.openModal(Toast, { text: t("test_rule_modal.failed_to_test") });
    }
  };

  return (
    <BaseModal
      headerClassName={styles.title}
      header={<ModalHeader closeModal={closeModal}>{modalTitle}</ModalHeader>}
      footer={
        <ModalButtons
          testId="test-rule-save-button"
          onSubmit={() => closeModal({ condition: conditionState })}
          onCancel={() => closeModal()}
          submitText={t("destinations_page.save")}
        />
      }
      closeModal={closeModal}
      className={styles.testRuleModal}
      bodyClassName={styles.modalBody}
      testId="test-rule-modal"
    >
      <div className={styles.conditionSettings}>
        {isExpression && (
          <>
            <div className={styles.fieldRow}>
              <div className={styles.fieldHeader}>{t("test_regex_modal.test_regex_expression")}</div>
              <div className={styles.expressionContainer}>
                <span className={styles.expressionPrefix}>/</span>
                <TextInput
                  data-testid="test-rule-expression"
                  removeClear={true}
                  className={styles.expressionInput}
                  placeholder={t("dlp_page.conditions.expression")}
                  defaultValue={(condition as DLPRuleExpressionType).expression}
                  paddingInline="none"
                  onChange={(e) => setConditionState({ ...conditionState, expression: e.target.value } as DLPRuleExpressionType)}
                />
                <span className={styles.expressionSuffix}>/g{(conditionState as DLPRuleExpressionType).caseSensitive ? "" : "i"}</span>
              </div>
            </div>
            <div className={styles.fieldsRow}>
              <div className={styles.fieldRow}>
                <div className={styles.fieldHeader}>{t("dlp_page.conditions.max_occurrences")}</div>
                <TextInput
                  data-testid="test-rule-max"
                  paddingInline="none"
                  type="number"
                  defaultValue={(condition as DLPRuleExpressionType).max}
                  onChange={(e) => setConditionState({ ...conditionState, max: Number(e.target.value) } as DLPRuleExpressionType)}
                />
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.fieldHeader}>{t("dlp_page.conditions.expression_casesensitive")}</div>
                <Select
                  paddingInline="none"
                  data-testid="test-rule-case-sensitive"
                  controlClassName={styles.caseSensitiveSelect}
                  value={
                    (conditionState as DLPRuleExpressionType).caseSensitive ? CASE_SENSITIVE_SELECT_OPTIONS[0] : CASE_SENSITIVE_SELECT_OPTIONS[1]
                  }
                  onChange={(newValue: (typeof CASE_SENSITIVE_SELECT_OPTIONS)[number]) =>
                    setConditionState({ ...conditionState, caseSensitive: newValue.value } as DLPRuleExpressionType)
                  }
                  options={CASE_SENSITIVE_SELECT_OPTIONS}
                />
              </div>
            </div>
          </>
        )}

        {isPII && (
          <div className={styles.fieldsRow}>
            <div className={styles.fieldRow}>
              <div className={styles.fieldHeader}>{t("test_pii_modal.pii_category")}</div>
              <Select
                paddingInline="none"
                data-testid="test-rule-pii-category"
                controlClassName={styles.categorySelect}
                isSearchable={true}
                placeholder={t("dlp_page.search_placeholder")}
                value={
                  (conditionState as DLPRulePIIType).category ? { value: (conditionState as DLPRulePIIType).category, label: categoryLabel } : null
                }
                onChange={(val: { value: string; label: string } | null) =>
                  setConditionState({ ...conditionState, category: val?.value ?? "" } as DLPRulePIIType)
                }
                options={PIICategories}
              />
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.fieldHeader}>{t("dlp_page.conditions.max_occurrences")}</div>
              <TextInput
                data-testid="test-rule-max"
                paddingInline="none"
                type="number"
                defaultValue={(condition as DLPRulePIIType).max}
                onChange={(e) => setConditionState({ ...conditionState, max: Number(e.target.value) } as DLPRulePIIType)}
              />
            </div>
          </div>
        )}

        {isMetadata && (
          <div className={styles.fieldsRow}>
            <div className={styles.fieldRow}>
              <div className={styles.fieldHeader}>{t("dlp_page.conditions.metadata_key")}</div>
              <TextInput
                data-testid="test-rule-metadata-key"
                removeClear={true}
                placeholder={t("dlp_page.conditions.metadata_key")}
                className={styles.metadataInput}
                value={(conditionState as DLPRuleMetaDataType).key}
                paddingInline="none"
                onChange={(e) => {
                  setConditionState({ ...conditionState, key: e.target.value } as DLPRuleMetaDataType);
                  setMetadataTestResult(undefined);
                }}
              />
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.fieldHeader}>{t("dlp_page.conditions.metadata_value")}</div>
              <TextInput
                data-testid="test-rule-metadata-value"
                removeClear={true}
                placeholder={t("dlp_page.conditions.metadata_value")}
                className={styles.metadataInput}
                value={(conditionState as DLPRuleMetaDataType).value}
                paddingInline="none"
                onChange={(e) => {
                  setConditionState({ ...conditionState, value: e.target.value } as DLPRuleMetaDataType);
                  setMetadataTestResult(undefined);
                }}
              />
            </div>
          </div>
        )}
      </div>

      {isMetadata && (
        <div className={styles.metadataPhases} data-testid="test-rule-metadata-file">
          <div className={styles.metadataPhase}>
            <div className={styles.fieldHeader}>{t("test_rule_modal.upload_file_to_test_metadata")}</div>
            <UploadFilesV2
              files={metadataFile ? [metadataFile] : undefined}
              multiple={false}
              accept="*/*"
              showClear={true}
              onChange={(files) => {
                setMetadataFile(files[0] ?? null);
                setMetadataTestResult(undefined);
                setExtractedMetadata(null);
              }}
              onClear={() => {
                setMetadataFile(null);
                setMetadataTestResult(undefined);
                setExtractedMetadata(null);
              }}
            />
            <Button
              text={t("test_rule_modal.load")}
              onClick={loadMetadata}
              isSubmitting={loading}
              disabled={!metadataFile}
              className={styles.loadButton}
              testId="test-rule-load-metadata-button"
            />
          </div>

          {extractedMetadata && Object.keys(extractedMetadata).length > 0 && (
            <div className={styles.metadataPhase}>
              <div className={styles.extractedMetadataSection}>
                <div className={styles.fieldHeader}>{t("test_rule_modal.extracted_metadata")}</div>
                <pre className={styles.extractedMetadataPre} data-testid="test-rule-extracted-metadata">
                  {Object.entries(extractedMetadata)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join("\n")}
                </pre>
              </div>
              <div className={styles.metadataPhaseHeaderRow}>
                <div className={styles.fieldHeader}>{t("test_rule_modal.test_metadata_phase")}</div>
                {metadataTestResult !== undefined && (
                  <div
                    className={classNames(styles.metadataResultIcon, {
                      [styles.succeed]: metadataTestResult,
                      [styles.failed]: !metadataTestResult,
                    })}
                    data-testid={`test-rule-metadata-result-${metadataTestResult ? "match" : "no-match"}`}
                  >
                    <TestResultStatus succeed={metadataTestResult} />
                  </div>
                )}
              </div>
              <Button text={t("test_pii_modal.test_button")} onClick={runTest} className={styles.testButton} testId="test-rule-test-button" />
            </div>
          )}
        </div>
      )}

      <div className={classNames(styles.testTextsSection, { [styles.hidden]: isMetadata })}>
        <div className={styles.testRowsContainer}>
          {rows.map((row) => (
            <div
              key={row.id}
              data-testid="test-rule-text-container"
              className={classNames(styles.testTextContainer, {
                [styles.succeed]: row.result === true,
                [styles.failed]: row.result === false,
                [styles.loading]: loading,
              })}
            >
              <TextareaAutosize
                data-testid="test-rule-text-area"
                placeholder={t("test_pii_modal.text_to_test_placeholder")}
                value={row.text}
                className={styles.testTextArea}
                minRows={2}
                maxRows={4}
                onChange={(e) => updateRowText(row.id, e.target.value)}
              />
              <div className={styles.rowActions}>
                <TestResultStatus succeed={row.result} />
                {rows.length > 1 && (
                  <button type="button" className={styles.removeRowButton} onClick={() => removeRow(row.id)} data-testid="test-rule-remove-row">
                    <IoMdClose />
                  </button>
                )}
              </div>
            </div>
          ))}
          {rows.length < MAX_TEST_ROWS && (
            <button type="button" className={styles.addRowButton} onClick={addRow} data-testid="test-rule-add-row">
              <IoMdAdd />
            </button>
          )}
        </div>
      </div>

      {!isMetadata && (
        <Button
          text={t("test_pii_modal.test_button")}
          onClick={runTest}
          isSubmitting={loading}
          disabled={isPII && !(conditionState as DLPRulePIIType).category}
          className={styles.testButton}
          testId="test-rule-test-button"
        />
      )}
    </BaseModal>
  );
});
